import {
  useRef,
  useState,
  type FC,
  type MouseEvent,
  type ReactNode,
} from "react";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "@/shared/hooks";
import { getMinPrice } from "@/shared/utils/getMinPrice";
import {
  getPaymentButtonText,
  type PaymentScreenProduct,
} from "@/shared/components/ContentPlayer/PaymentScreen";
import { useProduct } from "@/entities/products";
import { ProductPayment } from "@/widgets/ProductScreen/ProductPayment";
import { ProductHero } from "@/widgets/ProductScreen/ProductHero";
import { ASSISTANT_Z_INDEX } from "@/features/assistant";

// Ниже PaymentDialog и PaymentStatusDialog (ASSISTANT_Z_INDEX - 30): окно
// оплаты открывается поверх этой карточки, а на дефолтном слое MUI (1300)
// карточка накрывала его. Выше Header (1200) и RouteDialog (1199), потому
// что открывается из окна стрима или видео.
const PURCHASE_DIALOG_Z_INDEX = ASSISTANT_Z_INDEX - 40;

type Screen =
  | { screen: "choose"; products: PaymentScreenProduct[] }
  | { screen: "product"; productId: string };

const DialogHeader: FC<{ title: ReactNode; onClose: () => void }> = ({
  title,
  onClose,
}) => (
  <DialogTitle
    component="div"
    sx={{ display: "flex", alignItems: "center", gap: 2, pr: 1.5 }}
  >
    <Typography variant="h6" component="h2" sx={{ flex: 1, minWidth: 0 }}>
      {title}
    </Typography>
    <IconButton onClick={onClose} size="small" aria-label="close">
      <CloseIcon />
    </IconButton>
  </DialogTitle>
);

/**
 * Экран «Выберите продукт» (порт ProductDialog/ChooseProduct.js): карточки
 * продуктов одного типа с названием и минимальной ценой.
 */
const ChooseProduct: FC<{
  products: PaymentScreenProduct[];
  onPick: (productId: string) => void;
}> = ({ products, onPick }) => {
  const { t } = useTranslation();
  return (
    <Stack spacing={1.5}>
      {products.map((product) => {
        const minPrice = getMinPrice(product.prices);
        return (
          <Card key={product.id}>
            {/* Референс ChooseProduct.js:67-72: карточка это настоящая
                ссылка на страницу продукта (открывается в новой вкладке
                средним кликом или из контекстного меню), а обычный клик
                перехватывается и открывает продукт прямо в диалоге. */}
            <CardActionArea
              component="a"
              href={product.siteUrl}
              target="_blank"
              onClick={(event: MouseEvent<HTMLAnchorElement>) => {
                event.preventDefault();
                onPick(product.id);
              }}
            >
              <CardContent>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  {product.title}
                </Typography>
                {minPrice && (
                  <Typography variant="body1" sx={{ color: "primary.light" }}>
                    {getPaymentButtonText(t, product, minPrice)}
                  </Typography>
                )}
              </CardContent>
            </CardActionArea>
          </Card>
        );
      })}
    </Stack>
  );
};

/**
 * Экран продукта (порт ProductDialog/Product.js + ProductBody.js): та же
 * карточка цены, что на странице продукта (табы периодов, промокод,
 * условия, кнопка покупки), без ухода со страницы контента.
 */
const ProductPurchase: FC<{
  productId: string;
  onPurchased?: () => void;
  onClose: () => void;
}> = ({ productId, onPurchased, onClose }) => {
  const [promocodeId, setPromocodeId] = useState<string | null>(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoResetKey, setPromoResetKey] = useState(0);
  const succeeded = useRef(false);
  const { product, loading } = useProduct(productId, promocodeId);

  if (!product) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      <ProductPayment
        product={product}
        promoLoading={promoLoading || loading}
        setPromoLoading={setPromoLoading}
        onPromocodeApplied={(promocode) => setPromocodeId(promocode.id)}
        onPromocodeInvalid={() => {
          setPromocodeId(null);
          setPromoResetKey((key) => key + 1);
        }}
        promoResetKey={promoResetKey}
        onPaymentSucceeded={() => {
          succeeded.current = true;
          onPurchased?.();
        }}
        onPaymentDialogClosed={() => {
          if (succeeded.current) onClose();
        }}
      />
      {/* Референс Product.js:137 + ProductBody.js: под карточкой цены идёт
          описание продукта. Рендерим тем же блоком, что и страница продукта
          (ProductHero section="description"). */}
      <ProductHero product={product} section="description" />
    </Stack>
  );
};

/**
 * Покупка контента без ухода на страницу продукта, как в референсном
 * ProductDialog (useProductDialog.js): клик по цене открывает диалог с
 * карточкой продукта; если продуктов одного типа несколько, сначала
 * экран выбора продукта. После успешной оплаты родитель перезапрашивает
 * контент, чтобы плеер получил доступ, а диалог закрывается вместе с
 * окном оплаты.
 */
export function useContentPurchase(onPurchased?: () => void): {
  openProducts: (products: PaymentScreenProduct[]) => void;
  dialogs: ReactNode;
} {
  const { t } = useTranslation();
  const [screen, setScreen] = useState<Screen | null>(null);
  // Как PaymentDialog: на телефоне карточка во весь экран, на компьютере
  // обычное окно шириной sm.
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const openProducts = (products: PaymentScreenProduct[]) => {
    const [single] = products;
    if (products.length === 1 && single) {
      setScreen({ screen: "product", productId: single.id });
      return;
    }
    setScreen({ screen: "choose", products });
  };

  const close = () => setScreen(null);

  const dialogs = (
    <Dialog
      open={!!screen}
      onClose={close}
      fullScreen={fullScreen}
      maxWidth="sm"
      fullWidth
      sx={{ zIndex: PURCHASE_DIALOG_Z_INDEX }}
    >
      {screen?.screen === "choose" && (
        <>
          <DialogHeader
            title={t("payment.chooseProductTitle")}
            onClose={close}
          />
          <DialogContent>
            <ChooseProduct
              products={screen.products}
              onPick={(productId) =>
                setScreen({ screen: "product", productId })
              }
            />
          </DialogContent>
        </>
      )}
      {screen?.screen === "product" && (
        <>
          <DialogHeader
            title={<ProductTitle productId={screen.productId} />}
            onClose={close}
          />
          <DialogContent>
            <ProductPurchase
              productId={screen.productId}
              onPurchased={onPurchased}
              onClose={close}
            />
          </DialogContent>
        </>
      )}
    </Dialog>
  );

  return { openProducts, dialogs };
}

const ProductTitle: FC<{ productId: string }> = ({ productId }) => {
  const { product } = useProduct(productId);
  return <>{product?.title ?? ""}</>;
};
